import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { capitaliseFirstLetter } from "@shared/translations/src/format";
import ky from "ky";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useForm, type UseFormReturn } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useClickAway, useDebounce } from "react-use";
import { z } from "zod";
import {
  type ContactFormInitialValues,
  type ContactFormValues,
  type ContactSubmission,
  getLoqateError,
  type LoqateFindAddress,
  loqateFindAddressSchema,
  loqateRetrieveAddressSchema,
  contactSchema,
  SUPPLY_CHAIN_PARTNER_ROLES,
  type SupplyChainPartnerRole,
} from "./validation";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { SelectInput } from "@/components/SelectInput";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/command";
import { Form, FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { cn } from "@/lib/utils/common";
import {
  findSubdivision,
  getCountry,
  getCountryIdsByCode,
  getCountryOptions,
  getSubdivisionOptionsWithValue,
  toSupportedCountryCode,
} from "@/lib/utils/countries";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatEnumLabel, formatName } from "@/lib/utils/formatting";
import { toE164 } from "@/lib/utils/phone";

const LOQATE_FIND_URL = "https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws";
const LOQATE_RETRIEVE_URL = "https://api.addressy.com/Capture/Interactive/Retrieve/v1.2/json3.ws";
const ADDRESS_LIMIT = 6;

/**
 * The user's action is identical whatever went wrong with the lookup — a bad
 * key, an exhausted balance, an unserved country. The code that distinguishes
 * them is logged, not shown.
 */
const LOQATE_UNAVAILABLE_MESSAGE = "Address lookup unavailable for this country — enter it manually.";

/** Thrown once Loqate's failure has been logged, to carry the user-facing message. */
class LoqateUnavailableError extends Error {
  constructor() {
    super(LOQATE_UNAVAILABLE_MESSAGE);
    this.name = "LoqateUnavailableError";
  }
}

/**
 * Calls Loqate, turning its HTTP-200 error responses into thrown errors.
 *
 * Loqate reports its own failures as a 200 carrying a single item with an
 * `Error` code, which `ky` has no reason to reject.
 */
const postToLoqate = async (url: string, searchParams: Record<string, string | number>) => {
  const response = await ky.post(url, { searchParams }).json();
  const loqateError = getLoqateError(response);

  if (loqateError) {
    console.error("Loqate lookup failed", {
      code: loqateError.Error,
      description: loqateError.Description,
      cause: loqateError.Cause,
      resolution: loqateError.Resolution,
    });

    throw new LoqateUnavailableError();
  }

  return response;
};

type BaseContactFormProps = {
  onSubmit: (values: ContactSubmission) => Promise<void>;
  onCancel?: () => void;
  initialData?: ContactFormInitialValues;
};

type ContactFormWithoutDelete = BaseContactFormProps & {
  onDelete?: undefined;
  isDeleting?: undefined;
};

type ContactFormWithDelete = BaseContactFormProps & {
  onDelete: () => void;
  isDeleting: boolean;
};

type ContactFormProps = ContactFormWithoutDelete | ContactFormWithDelete;

export function ContactForm({ onSubmit, onCancel, initialData, onDelete, isDeleting }: ContactFormProps) {
  const [showAdditionalAddressFields, setShowAdditionalAddressFields] = useState(Boolean(initialData));

  const { activeBusinessUnitIdInt, activeBusinessUnitCountryCode } = useActiveBusinessUnit();
  const { toast } = useToast();

  const {
    data: usersData,
    loading: userDataLoading,
    error: userDataError,
  } = useQuery(businessUnitUsersQuery, {
    variables: { businessUnitId: activeBusinessUnitIdInt },
  });

  // Fetched once when the form opens and used purely as a code-to-ID lookup at
  // save time — never rendered. See ADR-0001.
  const { data: countriesData, loading: countriesLoading, error: countriesError } = useQuery(countriesQuery);

  const apiCountries = removeNothings(countriesData?.countries || []);
  const countryOptions = getCountryOptions(apiCountries);
  const countryIdsByCode = getCountryIdsByCode(apiCountries);

  const userOptions: ComboboxOption[] = removeNothings(usersData?.businessUnitUsers || []).map((businessUnitUser) => {
    return {
      label: formatName({ firstName: businessUnitUser.user.firstName, lastName: businessUnitUser.user.lastName }),
      value: businessUnitUser.user.id,
    };
  });

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      businessName: initialData?.businessName || "",
      accountNumber: initialData?.accountNumber || "",
      contactFirstName: initialData?.contactFirstName || "",
      contactLastName: initialData?.contactLastName || "",
      contactEmail: initialData?.contactEmail || "",
      contactPhone: initialData?.contactPhone || "",
      businessAccountManagerId: initialData?.businessAccountManagerId || "",
      type: initialData?.type || "",
      role: initialData?.role || undefined,
      addressLine1: initialData?.addressLine1 || "",
      addressLine2: initialData?.addressLine2 || "",
      city: initialData?.city || "",
      state: initialData?.state || "",
      postCode: initialData?.postCode || "",
      countryCode: initialData?.countryCode || undefined,
      monthlyTarget: initialData?.monthlyTarget || undefined,
      note: initialData?.note || "",
    },
  });

  const countryCode = form.watch("countryCode");

  // The Business Unit's country is the default for a new Contact, applied once
  // the query lands. It is nullable on the API — when it is absent, or absent
  // from this backend's country table, the picker stays empty rather than
  // guessing.
  const businessUnitCountryCode = toSupportedCountryCode(activeBusinessUnitCountryCode);
  const defaultCountryCode =
    businessUnitCountryCode && countryIdsByCode[businessUnitCountryCode] ? businessUnitCountryCode : undefined;

  useEffect(() => {
    if (!initialData && !countryCode && defaultCountryCode) {
      form.setValue("countryCode", defaultCountryCode);
    }
  }, [countryCode, defaultCountryCode, initialData, form]);

  const addressValidationError = Boolean(
    form.formState.errors.addressLine1 ||
      form.formState.errors.city ||
      form.formState.errors.state ||
      form.formState.errors.postCode ||
      form.formState.errors.countryCode,
  );

  const roleOptions = getRoleOptions(SUPPLY_CHAIN_PARTNER_ROLES);
  const {
    data: supplyChainPartnerTypeData,
    loading: supplyChainPartnerTypeDataLoading,
    error: supplyChainPartnerTypeDataError,
  } = useQuery(supplyChainPartnerTypesQuery);

  // Filter types based on selected role
  const allTypes = removeNothings(supplyChainPartnerTypeData?.supplyChainPartnerTypes?.edges || []);
  const filteredTypes = form.watch("role") ? allTypes.filter((edge) => edge?.node?.role === form.watch("role")) : [];

  const supplyChainPartnerTypeOptions = getTypeOptions(filteredTypes);

  const handleEnterAddressManually = () => {
    // Render the address fields synchronously so they can receive focus straight away
    flushSync(() => setShowAdditionalAddressFields(true));
    form.setFocus("addressLine1");
  };

  const handleAddressSelected = () => {
    setShowAdditionalAddressFields(true);
    form.setFocus("contactFirstName");
  };

  const handleCancel = () => {
    form.reset();
    setShowAdditionalAddressFields(false);

    if (onCancel) {
      onCancel();
    }
  };

  /**
   * Resolves the two save-time concerns the schema deliberately leaves alone:
   * the country's backend ID, and the phone number's canonical form.
   *
   * A country with no ID in this backend is never saved — the picker should not
   * have offered it, so reaching here means the country list changed underfoot.
   */
  const handleValidSubmit = async (values: ContactFormValues) => {
    const countryId = countryIdsByCode[values.countryCode];

    if (!countryId) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: "That country is not available in this region. Pick another, or try again.",
      });

      return;
    }

    await onSubmit({
      ...values,
      countryId,
      state: values.state || undefined,
      postCode: values.postCode.trim().toUpperCase(),
      contactPhone: toE164({ value: values.contactPhone, defaultCountry: values.countryCode }),
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleValidSubmit)} className="grid grid-cols-1 md:grid-cols-3 gap-x-3">
        <FormField
          control={form.control}
          name="businessName"
          render={({ field }) => (
            <FormItemWithMessage
              label="Business Name *"
              // A new contact opens ready to type; editing keeps the default focus
              inputComponent={<Input placeholder="Enter business name" autoFocus={!initialData} {...field} />}
            />
          )}
        />
        <FormField
          control={form.control}
          name="accountNumber"
          render={({ field }) => (
            <FormItemWithMessage
              label="Account Number"
              inputComponent={<Input placeholder="Enter account number" {...field} />}
            />
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItemWithMessage
              label="Role *"
              inputComponent={
                <SelectInput
                  {...field}
                  placeholder="Select role"
                  options={roleOptions}
                  value={field.value || ""}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value) {
                      // Filter types by the selected role
                      const matchingTypes = allTypes.filter((edge) => edge?.node?.role === value);
                      const typeOptions = getTypeOptions(matchingTypes);
                      // Set type to first option if available, otherwise empty string
                      form.setValue("type", (typeOptions.length > 0 ? typeOptions[0]?.value : "") || "");
                    } else {
                      form.setValue("type", "");
                    }
                  }}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItemWithMessage
              label="Operation Type *"
              inputComponent={
                <SelectInput
                  {...field}
                  loading={supplyChainPartnerTypeDataLoading}
                  placeholder={
                    supplyChainPartnerTypeOptions.length === 0 ? "Please select a role first" : "Select type"
                  }
                  options={supplyChainPartnerTypeOptions}
                  disabled={
                    supplyChainPartnerTypeOptions.length === 0 ||
                    supplyChainPartnerTypeDataLoading ||
                    Boolean(supplyChainPartnerTypeDataError)
                  }
                  error={supplyChainPartnerTypeDataError}
                  value={field.value || ""}
                  onChange={(value) => {
                    // Ignore empty values from Radix UI's onValueChange initialization
                    // See: https://github.com/radix-ui/primitives/issues/3135
                    if (value) field.onChange(value);
                  }}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="monthlyTarget"
          render={({ field }) => (
            <FormItemWithMessage
              label="Monthly Target"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  allowNegative={false}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  maxLength={7}
                  decimalScale={0}
                  placeholder="Enter monthly target"
                />
              }
            />
          )}
        />
        <fieldset className="md:col-span-3 border-0 p-0 m-0">
          <legend
            className={cn(
              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 py-2",
              addressValidationError ? "text-red-500" : "",
            )}
          >
            Address *
          </legend>
          {/*
            The country sits above the search because both the search's
            `Countries` parameter and the Subdivision field depend on it.
          */}
          <div className="md:w-1/3 md:pr-3">
            <FormField
              control={form.control}
              name="countryCode"
              render={({ field }) => (
                <FormItemWithMessage
                  label="Country *"
                  inputComponent={
                    <SelectInput
                      {...field}
                      loading={countriesLoading}
                      error={countriesError}
                      disabled={countriesLoading || Boolean(countriesError)}
                      placeholder="Select country"
                      options={countryOptions}
                      value={field.value || ""}
                      onChange={(value) => {
                        if (value === field.value) return;

                        field.onChange(value);
                        // A Subdivision only means anything within its country. Cleared with "" because
                        // React Hook Form's controlled fields ignore undefined and keep showing the old value.
                        form.setValue("state", "");
                      }}
                    />
                  }
                />
              )}
            />
          </div>
          {showAdditionalAddressFields ? null : (
            <AddressSearch
              key={countryCode}
              hasError={addressValidationError}
              form={form}
              countryCode={countryCode}
              onEnterManually={handleEnterAddressManually}
              onAddressSelected={handleAddressSelected}
            />
          )}

          <div
            className={cn(
              "grid transition-all duration-300 ease-out",
              showAdditionalAddressFields ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col">
                {showAdditionalAddressFields && <AddressFields form={form} countryCode={countryCode} />}
              </div>
            </div>
          </div>
        </fieldset>
        <fieldset className="md:col-span-3 border-0 p-0 m-0">
          <legend className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 py-2">
            Principal Contact
          </legend>
          <div className="grid md:grid-cols-3 gap-x-3">
            <div className="grid grid-cols-2 gap-x-3 md:col-span-2">
              <FormField
                control={form.control}
                name="contactFirstName"
                render={({ field }) => (
                  <FormItemWithMessage
                    label="First Name"
                    inputComponent={<Input placeholder="Enter first name" {...field} />}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="contactLastName"
                render={({ field }) => (
                  <FormItemWithMessage
                    label="Last Name"
                    inputComponent={<Input placeholder="Enter last name" {...field} />}
                  />
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="contactPhone"
              render={({ field }) => (
                <FormItemWithMessage
                  label="Phone Number"
                  inputComponent={<Input type="tel" placeholder="Enter phone number" {...field} />}
                />
              )}
            />
            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItemWithMessage
                  label="Email"
                  inputComponent={<Input type="email" placeholder="info@contact.com" {...field} />}
                />
              )}
            />
          </div>
        </fieldset>
        <FormField
          control={form.control}
          name="businessAccountManagerId"
          render={({ field }) => (
            <FormItemWithMessage
              label="Business Account Manager"
              inputComponent={
                <Combobox
                  type="single"
                  loading={userDataLoading}
                  error={userDataError}
                  placeholder="Select Manager"
                  options={userOptions}
                  selected={field.value}
                  onChange={(value) => form.setValue("businessAccountManagerId", value || "")}
                  ref={field.ref}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItemWithMessage
              label="Note"
              className="md:col-span-3"
              inputComponent={<Textarea placeholder="Enter note" className="resize-none" {...field} />}
            />
          )}
        />
        <div
          className={cn(
            "md:col-span-full mt-5 flex flex-col md:flex-row ",
            Boolean(onDelete) ? "md:justify-between" : "md:justify-end",
          )}
        >
          {onDelete ? (
            <Button
              disabled={form.formState.isSubmitting || isDeleting}
              type="button"
              variant="destructive"
              onClick={onDelete}
              className="w-full md:w-auto"
              isLoading={isDeleting}
            >
              Delete
            </Button>
          ) : null}
          <div className="md:flex md:gap-2">
            <Button
              disabled={form.formState.isSubmitting || isDeleting}
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="w-full md:w-auto my-2 md:my-0"
            >
              Cancel
            </Button>
            <Button
              isLoading={form.formState.isSubmitting}
              disabled={form.formState.isSubmitting || isDeleting}
              type="submit"
              variant="confirm"
              className="w-full md:w-auto"
            >
              {initialData ? "Save Changes" : "Save and Create"}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function AddressSearch({
  hasError,
  form,
  countryCode,
  onEnterManually,
  onAddressSelected,
}: {
  hasError: boolean;
  form: UseFormReturn<ContactFormValues>;
  countryCode: ContactFormValues["countryCode"] | undefined;
  onEnterManually: () => void;
  onAddressSelected: () => void;
}) {
  const { toast } = useToast();
  const [addressList, setAddressList] = useState<LoqateFindAddress["Items"]>([]);
  const [isAddressOptionsVisible, setIsAddressOptionsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const commandRef = useRef<HTMLDivElement>(null);
  const lookupIsActive = useRef(true);

  const country = getCountry(countryCode);
  const isSearchable = Boolean(country?.loqateSupported);

  useEffect(() => {
    lookupIsActive.current = true;

    return () => {
      lookupIsActive.current = false;
    };
  }, []);

  const isCurrentCountry = (requestCountryCode: ContactFormValues["countryCode"]) =>
    lookupIsActive.current && form.getValues("countryCode") === requestCountryCode;

  useClickAway(commandRef, () => {
    setIsAddressOptionsVisible(false);
  });

  /**
   * One message for every way the lookup can fail, because the user's next
   * action is the same in all of them: enter the address by hand. What
   * distinguishes a credit problem from a licence problem from a schema drift
   * is logged, not shown.
   */
  const showLookupFailure = (error: unknown) => {
    console.error("Address lookup failed", error);

    toast({
      variant: "destructive",
      title: "Uh oh! Something went wrong.",
      description:
        error instanceof LoqateUnavailableError || error instanceof z.ZodError
          ? LOQATE_UNAVAILABLE_MESSAGE
          : getErrorMessage(error) || "There was a problem with your request.",
    });
  };

  const handleFindAddress = async (searchTerm: string) => {
    if (!countryCode) return;

    const requestCountryCode = countryCode;
    setIsLoading(true);

    try {
      const response = await postToLoqate(LOQATE_FIND_URL, {
        key: import.meta.env.VITE_LOQATE_API_KEY,
        text: searchTerm,
        countries: countryCode,
        limit: ADDRESS_LIMIT,
      });

      const parsedResponse = loqateFindAddressSchema.parse(response);

      if (!isCurrentCountry(requestCountryCode)) return;

      setAddressList(parsedResponse.Items.filter((addr) => addr.Type === "Address"));
    } catch (error: unknown) {
      if (isCurrentCountry(requestCountryCode)) {
        showLookupFailure(error);
      }
    } finally {
      if (isCurrentCountry(requestCountryCode)) {
        setIsLoading(false);
      }
    }
  };

  const handleAddressSelect = async (id: string) => {
    if (!countryCode) return;

    const requestCountryCode = countryCode;

    try {
      const response = await postToLoqate(LOQATE_RETRIEVE_URL, {
        key: import.meta.env.VITE_LOQATE_API_KEY,
        id: id,
      });

      const parsedResponse = loqateRetrieveAddressSchema.parse(response);

      if (!isCurrentCountry(requestCountryCode)) return;

      const address = parsedResponse.Items[0];

      if (!address) {
        toast({
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: "Error selecting address.",
        });

        return;
      }

      // Loqate identifies a Subdivision by code in some countries and by name in
      // others. Match on either, and keep whatever it returned if neither
      // matches — dropping it would lose the only Subdivision we have.
      const province = address.ProvinceCode || address.ProvinceName || address.Province;
      const subdivision = findSubdivision({ countryCode: requestCountryCode, value: province });

      form.setValue("addressLine1", address.Line1);
      form.setValue("addressLine2", address.Line2 || "");
      form.setValue("city", address.City);
      form.setValue("postCode", address.PostalCode);
      form.setValue("state", subdivision?.code ?? province ?? "");

      setIsAddressOptionsVisible(false);
      setAddressList([]);
      onAddressSelected();
    } catch (error: unknown) {
      if (isCurrentCountry(requestCountryCode)) {
        showLookupFailure(error);
      }
    }
  };

  useDebounce(
    () => {
      const trimmedSearchTerm = searchTerm.trim();
      if (trimmedSearchTerm.length >= 5 && isSearchable) {
        setIsAddressOptionsVisible(true);
        void handleFindAddress(trimmedSearchTerm);
      } else {
        setIsAddressOptionsVisible(false);
        setAddressList([]);
      }
    },
    500,
    [searchTerm, countryCode],
  );

  return (
    <>
      <div className="flex flex-col md:flex-row items-center gap-x-3">
        <div className="relative w-full">
          <Command
            ref={commandRef}
            className={cn("rounded-lg border border-input", "[&_[cmdk-input-wrapper]]:border-b-0")}
          >
            <CommandInput
              placeholder={
                countryCode
                  ? isSearchable
                    ? "Search for address..."
                    : LOQATE_UNAVAILABLE_MESSAGE
                  : "Select a country first"
              }
              disabled={!isSearchable}
              onValueChange={(value) => {
                setSearchTerm(value);
              }}
              onFocus={() => {
                if (addressList.length > 0) {
                  setIsAddressOptionsVisible(true);
                }
              }}
            />

            {isAddressOptionsVisible && (
              <div className="absolute top-full left-0 right-0 z-50">
                <CommandList>
                  <div className="w-full rounded-md border border-border bg-background shadow-sm">
                    {isLoading ? (
                      <CommandLoading>
                        <Spinner />
                      </CommandLoading>
                    ) : (
                      <>
                        <CommandEmpty>No addresses found</CommandEmpty>
                        <CommandGroup className="md:max-h-[150px] overflow-y-auto">
                          {addressList.map((address) => (
                            <CommandItem
                              key={address.Id}
                              value={`${address.Text} ${address.Description}`}
                              onSelect={() => void handleAddressSelect(address.Id)}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{address.Text}</span>
                                <span className="text-sm text-gray-500 italic">{address.Description}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </div>
                </CommandList>
              </div>
            )}
          </Command>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full md:w-1/3 h-10 mt-2 md:mt-0 border-input"
          onClick={() => {
            // Carry over what was typed so a failed search doesn't mean starting again,
            // without clobbering a line 1 the user already has.
            const trimmedSearchTerm = searchTerm.trim();
            if (trimmedSearchTerm && !form.getValues("addressLine1")) {
              form.setValue("addressLine1", trimmedSearchTerm);
            }
            onEnterManually();
          }}
        >
          Enter Manually
        </Button>
      </div>
      <div className="min-h-[20px] mt-2">
        {hasError ? (
          <p className="text-[0.8rem] font-medium text-red-500 dark:text-red-900">Address is required.</p>
        ) : null}
      </div>
    </>
  );
}

export function AddressFields({
  form,
  countryCode,
}: {
  form: UseFormReturn<ContactFormValues>;
  countryCode: ContactFormValues["countryCode"] | undefined;
}) {
  const country = getCountry(countryCode);
  // Until a country is chosen there is no country-specific word for either
  // field, and the fields are disabled anyway — so the fallbacks name the
  // concept without assuming a country.
  const subdivisionLabel = country?.subdivisionLabel ?? "State / Province";
  const postCodeLabel = country?.postCodeLabel ?? "Postcode";

  return (
    <>
      <FormField
        control={form.control}
        name="addressLine1"
        render={({ field }) => (
          <FormItemWithMessage
            label="Address Line 1 *"
            inputComponent={<Input placeholder="Enter address line 1" {...field} />}
          />
        )}
      />
      <FormField
        control={form.control}
        name="addressLine2"
        render={({ field }) => (
          <FormItemWithMessage
            label="Address Line 2"
            inputComponent={<Input placeholder="Enter address line 2" {...field} />}
          />
        )}
      />
      <FormField
        control={form.control}
        name="postCode"
        render={({ field }) => (
          <FormItemWithMessage
            label={`${postCodeLabel} *`}
            inputComponent={<Input placeholder={`Enter ${postCodeLabel}`} {...field} />}
          />
        )}
      />
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItemWithMessage label="City *" inputComponent={<Input placeholder="Enter city" {...field} />} />
        )}
      />
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItemWithMessage
            label={`${subdivisionLabel}${country?.subdivisionRequired ? " *" : ""}`}
            inputComponent={
              <SelectInput
                {...field}
                placeholder={countryCode ? `Select ${subdivisionLabel}` : "Select a country first"}
                disabled={!countryCode}
                options={getSubdivisionOptionsWithValue({ countryCode, value: field.value })}
                value={field.value || ""}
              />
            }
          />
        )}
      />
    </>
  );
}

export const getRoleOptions = (roles: readonly SupplyChainPartnerRole[]) => {
  return roles.map((role) => ({
    value: role,
    label: formatEnumLabel(role),
  }));
};

type SupplyChainPartnerType = NonNullable<
  NonNullable<ResultOf<typeof supplyChainPartnerTypesQuery>>["supplyChainPartnerTypes"]
>["edges"];

export const getTypeOptions = (edges: SupplyChainPartnerType): ComboboxOption[] => {
  return edges.map((edge) => {
    const node = edge?.node;
    const id = node?.id || "";
    const label = capitaliseFirstLetter(node?.label || "");

    return {
      value: id,
      label: label,
    };
  });
};

export const businessUnitUsersQuery = graphql(`
  query BusinessUnitUsers($businessUnitId: Int) {
    businessUnitUsers(businessUnitId: $businessUnitId) {
      id
      user {
        id
        firstName
        lastName
      }
    }
  }
`);

export const supplyChainPartnerTypesQuery = graphql(`
  query SupplyChainPartnerTypes {
    supplyChainPartnerTypes {
      edges {
        node {
          id
          label
          role
        }
      }
    }
  }
`);

export const countriesQuery = graphql(`
  query Countries {
    countries {
      id
      isoAlpha2
    }
  }
`);
